import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { useGraphQL } from "../../../hooks/useGraphQL";
import marketplaceABI from "../../../abis/FunooniGlobalMArket.json";

const GET_LISTING = `
  query GetListing($id: ID!) {
    listing(id: $id) {
      id
      listingId
      seller
      pricePerToken
      quantity
      tokenId
      nft {
        id
      }
    }
  }
`;

export default function PurchaseForm({ selectedListingId, onListingChange }) {
  const [listingId, setListingId] = useState(selectedListingId || "");
  const [quantity, setQuantity] = useState(1);
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [txStatus, setTxStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const { data } = useGraphQL(GET_LISTING, { id: listingId });

  useEffect(() => {
    if (selectedListingId) setListingId(selectedListingId);
    checkWalletConnection();
  }, [selectedListingId]);

  const handleListingIdChange = (e) => {
    const newId = e.target.value;
    setListingId(newId);
    if (onListingChange) onListingChange(newId);
  };

  const checkWalletConnection = async () => {
    if (window.ethereum) {
      const web3Provider = new ethers.BrowserProvider(window.ethereum);
      setProvider(web3Provider);
      try {
        const accounts = await web3Provider.send("eth_accounts", []);
        if (accounts.length > 0) setAccount(accounts[0]);
      } catch (err) {
        console.error("Error checking wallet connection:", err);
      }
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask!");
      return;
    }
    try {
      const web3Provider = new ethers.BrowserProvider(window.ethereum);
      setProvider(web3Provider);
      const accounts = await web3Provider.send("eth_requestAccounts", []);
      setAccount(accounts[0]);
    } catch (err) {
      console.error("Wallet connection failed:", err);
    }
  };
  const formatAddress = (address) =>
    `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;

  const handlePurchase = async () => {
    if (!listingId || quantity <= 0) return;
    if (!account) {
      await connectWallet();
      return;
    }
    if (!data?.listing) {
      alert("Listing not found");
      return;
    }

    const listing = data.listing;
    if (quantity > listing.quantity) {
      alert(`Maximum quantity: ${listing.quantity}`);
      return;
    }

    const pricePerToken = parseInt(listing.pricePerToken);
    const totalPrice = pricePerToken * quantity;

    try {
      setTxStatus("Confirm the transaction in your wallet...");
      setLoading(true);

      const signer = await provider.getSigner();
      const contract = new ethers.Contract(
        process.env.REACT_APP_MARKETPLACE_ADDRESS,
        marketplaceABI,
        signer,
      );

      const tx = await contract.buy(listing.listingId, quantity, {
        value: totalPrice,
      });

      setTxStatus(`Transaction sent: ${tx.hash}`);
      await tx.wait();
      setTxStatus(`Purchase completed! Transaction: ${tx.hash}`);
    } catch (err) {
      console.error(err);
      setTxStatus(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="purchase-section">
      <h2>Purchase NFT</h2>
      <div className="purchase-form">
        <div className="form-group">
          <label>Listing ID</label>
          <input
            type="text"
            value={listingId}
            onChange={handleListingIdChange}
            placeholder="Enter listing ID"
          />
        </div>

        {data?.listing && (
          <div className="listing-details">
            <h4>Listing Details</h4>
            <p>
              <strong>Collection:</strong> {formatAddress(data.listing.nft.id)}
            </p>
            <p>
              <strong>Token ID:</strong> {data.listing.tokenId}
            </p>
            <p>
              <strong>Seller:</strong> {formatAddress(data.listing.seller)}
            </p>
            <p>
              <strong>Price per token:</strong>{" "}
              {ethers.formatEther(data.listing.pricePerToken)} ETH
            </p>
            <p>
              <strong>Available:</strong> {data.listing.quantity}
            </p>
          </div>
        )}

        <div className="form-group">
          <label>Quantity</label>
          <input
            type="number"
            min="1"
            max={data?.listing?.quantity || 1}
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value))}
          />
        </div>

        {!account && (
          <div className="wallet-section">
            <button className="btn btn-secondary" onClick={connectWallet}>
              Connect Wallet
            </button>
          </div>
        )}

        {account && (
          <button
            className="btn btn-success"
            onClick={handlePurchase}
            disabled={!listingId || !quantity || loading}
          >
            {loading ? "Processing..." : "Buy"}
          </button>
        )}

        {account && (
          <p>
            Connected: {account.slice(0, 6)}...{account.slice(-4)}
          </p>
        )}

        {txStatus && <div className="tx-status">{txStatus}</div>}
      </div>
    </section>
  );
}
